import { ReactNode, useMemo } from 'react';
import { DynamicContextProvider } from '@dynamic-labs/sdk-react-core';
import { SolanaWalletConnectors } from '@dynamic-labs/solana';

import { AdminModeProvider } from '../lib/admin-mode-context';
import { CodeFlavorProvider } from '../lib/code-flavor-context';
import { DYNAMIC_CSS_OVERRIDES } from '../lib/dynamic-overrides';
import { NetworkProvider, useNetwork } from '../lib/network-context';

// Public Dynamic environment ID for the SRSLY playground. Override via
// `DYNAMIC_ENVIRONMENT_ID` in playground/.env if you want to point at a
// different Dynamic project (e.g. a fork).
const DEFAULT_ENVIRONMENT_ID = '3369b2a8-d884-4d19-9d25-d941bb5a6e31';
const ENVIRONMENT_ID = process.env.DYNAMIC_ENVIRONMENT_ID || DEFAULT_ENVIRONMENT_ID;

interface Props {
  children: ReactNode;
}

export default function AppWalletProvider({ children }: Props) {
  return (
    <AdminModeProvider>
      <CodeFlavorProvider>
        <NetworkProvider>
          <DynamicShell>{children}</DynamicShell>
        </NetworkProvider>
      </CodeFlavorProvider>
    </AdminModeProvider>
  );
}

/**
 * Dynamic owns the wallet sign-in UI, embedded MPC key management, and
 * signer plumbing. We override its built-in Solana network configuration
 * so Dynamic's *internal* RPC calls (balance polling, signature status,
 * etc.) hit the user's selected RPC — without this, Dynamic always pings
 * `api.mainnet-beta.solana.com`, which 403s when a custom (e.g. Helius)
 * RPC is in use and bubbles up as an uncaught error overlay.
 *
 * We still own the actual transaction submit path
 * (`signTransaction` + `connection.sendRawTransaction` in
 * `useTransactionRunner`) so RPC selection always wins for the tx itself.
 */
function DynamicShell({ children }: { children: ReactNode }) {
  const { rpcUrl } = useNetwork();

  // Settings is read once on mount by Dynamic — re-rendering with a new
  // value won't reconfigure the SDK. We seed from the persisted rpcUrl
  // (already loaded from localStorage by `useNetworkConfig` synchronously)
  // so a fresh page load on the user's chosen network always points
  // Dynamic at the right host. Switching networks mid-session continues
  // to work for *our* code path; Dynamic's internal polls would then need
  // a refresh to follow — acceptable trade-off vs. remounting the
  // provider (which would drop the wallet session).
  const solanaNetworksOverrides = useMemo(
    () => [
      {
        blockExplorerUrls: ['https://explorer.solana.com'],
        chainId: '101',
        chainName: 'Solana',
        cluster: 'mainnet',
        iconUrls: ['https://app.dynamic.xyz/assets/networks/solana.svg'],
        isTestnet: false,
        name: 'Solana',
        nativeCurrency: {
          decimals: 9,
          iconUrl: 'https://app.dynamic.xyz/assets/networks/solana.svg',
          name: 'Solana',
          symbol: 'SOL',
        },
        networkId: 101,
        rpcUrls: [rpcUrl],
        privateCustomerRpcUrls: [rpcUrl],
        vanityName: 'Solana',
      },
    ],
    // Intentionally empty deps — we want the *initial* rpcUrl only; see
    // comment above. eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <DynamicContextProvider
      theme="dark"
      settings={{
        environmentId: ENVIRONMENT_ID,
        appName: 'SRSLY Playground',
        walletConnectors: [SolanaWalletConnectors],
        cssOverrides: DYNAMIC_CSS_OVERRIDES,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        overrides: { solNetworks: solanaNetworksOverrides } as any,
      }}
    >
      {children}
    </DynamicContextProvider>
  );
}

export const HAS_DYNAMIC_ENV = ENVIRONMENT_ID.length > 0;
